<template>
  <component
    :is="to ? 'router-link' : 'button'"
    :to="to"
    :type="type"
    :disabled="disabled"
    :class="buttonClasses"
    @click="handleClick">
    <fa-icon
      v-if="icon && !loading"
      :icon="icon"
      :class="[iconClasses, (text || $slots.default) && 'mr-1']" />
    <fa-icon
      v-if="loading"
      icon="spinner-third"
      spin
      :class="[iconClasses, (text || $slots.default) && 'mr-1']" />
    <span v-if="$slots.default || text">
      <slot>{{ text }}</slot>
    </span>
    <fa-icon v-if="showArrow && !loading" icon="arrow-right" class="ml-2" />
  </component>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  // Link/Button props
  to: {
    type: [String, Object],
    default: null
  },
  type: {
    type: String,
    default: 'button'
  },
  disabled: {
    type: Boolean,
    default: false
  },

  // Content props
  text: {
    type: String,
    default: ''
  },
  icon: {
    type: [String, Array],
    default: null
  },
  showArrow: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    default: false
  },

  // Style variants
  variant: {
    type: String,
    default: 'outline', // 'outline', 'gradient', 'solid'
    validator: (value) => ['outline', 'gradient', 'solid'].includes(value)
  },

  // Size
  size: {
    type: String,
    default: 'md', // 'sm', 'md', 'lg'
    validator: (value) => ['sm', 'md', 'lg'].includes(value)
  },

  // Full width
  fullWidth: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['click'])

const handleClick = (event) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}

const sizeClasses = computed(() => {
  const sizes = {
    sm: 'px-3 py-1 text-xs leading-5',
    md: 'px-3 py-1.5 text-sm leading-6',
    lg: 'px-4 py-2 text-base leading-7'
  }
  return sizes[props.size] || sizes.md
})

const variantClasses = computed(() => {
  const baseClasses =
    'flex justify-center items-center rounded-full font-medium transition-all duration-200 cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-black'

  const variants = {
    outline:
      'bg-transparent border text-white hover:bg-indigo-400 hover:border-indigo-400 hover:scale-105 active:scale-95 disabled:bg-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed disabled:hover:scale-100',
    gradient:
      'bg-gradient-to-r from-sky-500 to-indigo-500 text-white hover:from-sky-400 hover:to-indigo-400 hover:scale-105 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100',
    solid:
      'bg-white text-blue-600 hover:bg-gray-100 hover:scale-105 active:scale-95 disabled:bg-gray-500 disabled:text-gray-300 disabled:cursor-not-allowed disabled:hover:scale-100'
  }

  return `${baseClasses} ${variants[props.variant]}`
})

const buttonClasses = computed(() => {
  return [
    variantClasses.value,
    sizeClasses.value,
    props.fullWidth ? 'w-full' : ''
  ]
    .filter(Boolean)
    .join(' ')
})

const iconClasses = computed(() => {
  return props.size === 'sm'
    ? 'text-xs'
    : props.size === 'lg'
      ? 'text-base'
      : 'text-sm'
})
</script>
